<template>
  <Layout>
    <template #header-container>
      <div class="mc-list-header">
        <PageHeader isSticky :title="title">
          <template #afterTitle>
            <div class="d-flex gap-2">
              <McButtonBack href="/setting/webhook" />
              <McButton
                variant="primary"
                :text="$t('global.action.edit')"
                icon="icon-outline_edit"
                @click="$router.push(`/setting/webhook/update/${$route.params.id}`)"
              />
            </div>
          </template>
        </PageHeader>
      </div>
    </template>

    <div class="mc-webhook-form">
      <FormulateForm ref="formRef" v-model="formValues" class="mc-form" :errors="formErrors">
        <b-row class="g-0">
          <b-card class="mc-webhook-info__card">
            <b-row class="g-0">
              <div class="mc-webhook-info__input-group">
                <b-col md="2">
                  <h6 class="mc-general__label">
                    {{ $t('setting.list.webhook.url') }}
                  </h6>
                </b-col>
                <b-col md="12">
                  {{ url }}
                </b-col>
              </div>
            </b-row>
          </b-card>

          <h5 class="mc-general__label">
            {{ $t('global.object.alertList') }}
          </h5>
          <b-card>
            <b-row class="g-0">
              <div class="wall">
                <template v-for="category in eventCategories">
                  <div v-if="eventLists[category].length > 0" :key="category" class="item">
                    <b-row class="g-0">
                      <b-col md="6">
                        <h5 class="card-title">
                          {{ $t(`global.alert.type.${category}`) }}
                        </h5>
                      </b-col>
                      <b-card>
                        <div v-for="(item, index) in eventLists[category]" :key="index">
                          <b-row class="align-items-center mt-2">
                            <b-col md="1" class="d-flex justify-content-end">
                              <div class="form-check">
                                <input
                                  :id="`checkbox-${category}-${item.id}`"
                                  v-model="item.checked"
                                  class="form-check-input"
                                  type="checkbox"
                                  disabled="true"
                                />
                              </div>
                            </b-col>
                            <b-col md="11">
                              <label class="mb-0" :for="`checkbox-${category}-${item.id}`">
                                {{ item.title() }}
                              </label>
                            </b-col>
                          </b-row>
                        </div>
                      </b-card>
                    </b-row>
                  </div>
                </template>
              </div>
            </b-row>
          </b-card>
        </b-row>

        <h5 class="mc-general__label">
          {{ $t('global.object.fleetList') }}
        </h5>
        <b-row class="g-0">
          <McGrapqlTable
            ref="fleetTableRef"
            trackBy="id"
            :apiClient="query"
            :fields="fields"
            :customHandlers="customHandlers"
            :searchable="true"
            :withCard="true"
            :hasCheckbox="false"
            :activeFilters="activeFilters"
            :hasFilter="true"
            :hasDelete="false"
            :refreshData="isLoading"
            :isSearchWithFilter="true"
          >
            <template #button-filter="{ isSelected, selectedIds }">
              <ModalFilter
                :activeFilters="activeFilters"
                :isSelected="isSelected"
                :ids="selectedIds"
                :licensePlates="licensePlates"
                :searchWithFilter="true"
                @handleChangeActiveFilters="handleChangeActiveFilters"
                @refreshData="handleRefreshData"
              />
            </template>
            <template #cell(status)="data">
              <span v-if="data.item.status === 'I'" class="mc-status mc-status--green">
                {{ $t('global.active') }}
              </span>
              <span v-else-if="data.item.status === 'R'" class="mc-status mc-status--blue">
                {{ $t('global.repair') }}
              </span>
              <span v-else-if="data.item.status === 'A'" class="mc-status mc-status--red">
                {{ $t('global.archive') }}
              </span>
            </template>
          </McGrapqlTable>
        </b-row>
      </FormulateForm>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2.vue'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import ModalFilter from './ModalFilter'
import {
  listGeneral,
  listDMS,
  listDriverBehavior,
  listADAS,
  listDataUpdate,
  listFleetTask,
  listQuickAssignment,
  getTypeNames,
} from './lib/listAlert'
import { GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import * as webhookEventAPI from '@/services/rest-api-webhook/lib/event/event'
import * as webhookAPI from '@/services/rest-api-webhook/lib/webhook/webhook'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Setting',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    ModalFilter,
  },
  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          return this.onRowClicked(item)
        },
      },
      isLoading: false,
      formValues: {},
      formErrors: {},
      url: null,
      isResultSuccessful: false,
      activeFilters: {},
      activeEventCodes: [],
      licensePlates: [],
      selectedVehicleIds: [],
      selectedEventIds: [],
      listEventGroups: [],

      eventLists: {
        general: [],
        dataUpdate: [],
        dms: [],
        driverBehavior: [],
        adas: [],
        fleetTask: [],
        quickAssignment: [],
      },

      eventCategories: [
        'general',
        'driverBehavior',
        'dms',
        'adas',
        'dataUpdate',
        'fleetTask',
        'quickAssignment',
      ],

      eventMap: {
        general: {
          group: 'General',
          eventListVariableName: 'general',
          func: listGeneral,
        },
        dataUpdate: {
          group: 'Data Update',
          eventListVariableName: 'dataUpdate',
          func: listDataUpdate,
        },
        dms: {
          group: 'DMS',
          eventListVariableName: 'dms',
          func: listDMS,
        },
        driverBehavior: {
          group: 'Driver Behavior',
          eventListVariableName: 'driverBehavior',
          func: listDriverBehavior,
        },
        adas: {
          group: 'ADAS',
          eventListVariableName: 'adas',
          func: listADAS,
        },
        fleetTask: {
          group: 'Data Update',
          eventListVariableName: 'fleetTask',
          func: listFleetTask,
        },
        quickAssignment: {
          group: 'Data Update',
          eventListVariableName: 'quickAssignment',
          func: listQuickAssignment,
        },
      },

      vehicleIds: null,
    }
  },
  computed: {
    title() {
      return 'Detail ' + this.$t('setting.homePage.webhook')
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    query() {
      return {
        get: async (filter) => {
          await this.getDetailData()
          if (
            this.activeFilters.licensePlates === undefined ||
            this.activeFilters.licensePlates === null ||
            this.activeFilters.licensePlates === '' ||
            this.activeFilters.licensePlates === false
          ) {
            filter.licensePlates = this.licensePlates
          }
          const res = await this.$apollo.query({
            query: GET_VEHICLES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                ...this.activeFilters,
              },
            },
          })

          const pagination = res.data.vehiclesPaginated.vehiclesPaginated.pagination
          const data = res.data.vehiclesPaginated.vehiclesPaginated.data.filter((item) =>
            this.vehicleIds.has(item.id)
          )

          return { data, pagination }
        },
      }
    },
    fields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
          isSortable: false,
        },
        {
          key: 'vehicleType.type',
          label: this.$t('global.field.vehicleType'),
          isSortable: false,
        },
        {
          key: 'status',
          label: 'Status',
          isSortable: false,
        },
      ]
    },
  },
  methods: {
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)

        if (
          filter.values === null ||
          filter.values === undefined ||
          filter.values === '' ||
          filter.values === false
        )
          return

        if (Array.isArray(filter.values) && filter.values.length === 0) return

        if (filter.key === 'vehicleTypeId' && Array.isArray(filter.values)) {
          filter.values = filter.values.map((value) => parseInt(value))
        }

        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    handleRefreshData(value) {
      this.isLoading = value
      setTimeout(() => {
        this.isLoading = !value
      }, 300)
    },
    async getDetailData() {
      const res = await webhookAPI.getDetail(this.$route.params.id)
      this.url = res.data.url
      this.activeEventCodes = res.data.event.codes
      this.licensePlates = res.data.vehicle.license_plates
      this.vehicleIds = new Set(res.data.vehicle.ids)

      this.getEventList()
    },
    async getEventList() {
      const res = await webhookEventAPI.get()
      this.listEventGroups = res.data

      this.setLists()
    },

    setLists() {
      for (const category of this.eventCategories) {
        const eventMap = this.eventMap[category]
        var list = eventMap.func.call(this)
        const eventTypes = getTypeNames(this.listEventGroups, eventMap.group)

        const newList = list
          .filter((item) => item.code in eventTypes)
          .map((item) => ({
            ...item,
            id: eventTypes[item.code].event_id,
            checked: this.activeEventCodes.includes(item.code),
          }))

        this.$set(this.eventLists, eventMap.eventListVariableName, [...newList])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wall {
  column-count: 2;
  column-gap: 10px;
}
.item {
  display: inline-block;
  width: 100%;
  margin-bottom: 10px;
  box-sizing: border-box;
}
</style>
